import { Injectable } from '@angular/core';
import { CustomerDataService } from './customer-data.service';
import { IRatesTable } from '../interfaces/rates-table.interface';
import { ISimulationDataCDT } from '../interfaces/customer-data.interface';
import { EnumTemporality } from '../enums/temporality.enum';
import { MoreInfoService } from './more-info.service';

@Injectable({
  providedIn: 'root'
})
export class CdtCalculatorService {
  constructor(
    private data: CustomerDataService,
    private moreInfoService: MoreInfoService
  ) {}

  calculateTotalValueCDT(simulation: ISimulationDataCDT) {
    const simulationData = this.calculateFinantialReturns(simulation);

    return (
      simulationData.amount + simulationData.profit - simulationData.retention
    );
  }

  calculateFinantialReturns(
    simulationData: ISimulationDataCDT,
    controls?: any
  ): ISimulationDataCDT {
    if (
      !simulationData ||
      this.moreInfoService.moreInfoData.simulationPreviouslyUsed
    ) {
      simulationData = this.getDefaultData(controls);
    }
    if (this.data.customerData?.ratesTable?.length) {
      const rate = this.getRate(simulationData.amount, simulationData.days);
      simulationData.rate = rate;
      const X = +(1 + rate / 100).toPrecision(15);
      let Y = simulationData.days / 360;
      Y =
        this.earningsPeriod(simulationData.earningsPeriod) ===
        EnumTemporality.Cancelation
          ? Y
          : this.daysEarningsPeriod(simulationData.earningsPeriod) / 360;

      Y = +Y.toPrecision(15);
      const pow = +Math.pow(X, Y).toPrecision(15);
      const factor = +(pow - 1).toFixed(6);

      simulationData.profit = factor * simulationData.amount;
      simulationData.profit =
        this.earningsPeriod(simulationData.earningsPeriod) ===
        EnumTemporality.Cancelation
          ? simulationData.profit
          : (simulationData.days /
              this.daysEarningsPeriod(simulationData.earningsPeriod)) *
            simulationData.profit;

      simulationData.profit = +simulationData.profit.toFixed(0);
      const retentionPercentage =
        parseInt(this.moreInfoService.moreInfoData.retention, 10) / 100;
      simulationData.retention = +(
        simulationData.profit * retentionPercentage
      ).toFixed(0);
      simulationData.earnings =
        simulationData.profit - simulationData.retention;

      return simulationData;
    }
    return simulationData;
  }

  calculateFinantialReturnsFromReadSummaryTransaction(
    controls: any,
    rate: number
  ): ISimulationDataCDT {
    const simulationData = this.getDefaultData(controls);

    simulationData.rate = rate;
    const X = +(1 + rate / 100).toPrecision(15);
    let Y = simulationData.days / 360;
    Y =
      this.earningsPeriod(simulationData.earningsPeriod) ===
      EnumTemporality.Cancelation
        ? Y
        : this.daysEarningsPeriod(simulationData.earningsPeriod) / 360;

    Y = +Y.toPrecision(15);
    const pow = +Math.pow(X, Y).toPrecision(15);
    const factor = +(pow - 1).toFixed(6);

    simulationData.profit = factor * simulationData.amount;
    simulationData.profit =
      this.earningsPeriod(simulationData.earningsPeriod) ===
      EnumTemporality.Cancelation
        ? simulationData.profit
        : (simulationData.days /
            this.daysEarningsPeriod(simulationData.earningsPeriod)) *
          simulationData.profit;

    simulationData.profit = +simulationData.profit.toFixed(0);
    const retentionPercentage =
      parseInt(this.moreInfoService.moreInfoData.retention, 10) / 100;
    simulationData.retention = +(
      simulationData.profit * retentionPercentage
    ).toFixed(0);
    simulationData.earnings = simulationData.profit - simulationData.retention;

    return simulationData;
  }

  getDefaultData(controls: any) {
    return {
      days: controls.days,
      amount: controls.amount,
      earningsPeriod: controls.earningsPeriod,
      amountFormat: controls.amountFormat,
      rate: 0,
      profit: 0,
      earnings: 0,
      retention: 0,
      renovate: false
    };
  }

  getRate(amountValue: number, term: number): number {
    const ratesTable = this.data.customerData.ratesTable;
    const ratesLength = ratesTable.length;
    const amount = amountValue;
    const days = +term;
    let termByAmount: IRatesTable = {
      amount: null as unknown as number,
      term: []
    } as IRatesTable;
    let rate: number = null as unknown as number;

    for (let i = 0; i < ratesLength; i++) {
      termByAmount =
        amount >= ratesTable[i].amount ? ratesTable[i] : termByAmount;
    }

    if (termByAmount) {
      const termByAmountLength = termByAmount.term.length;

      for (let i = 0; i < termByAmountLength; i++) {
        rate =
          days >= termByAmount.term[i].days ? termByAmount.term[i].rate : rate;
      }
    }

    return rate;
  }

  earningsPeriod(period: number): EnumTemporality {
    return period in EnumTemporality ? period : EnumTemporality.Cancelation;
  }

  daysEarningsPeriod(period: number): number {
    switch (this.earningsPeriod(period)) {
      case EnumTemporality.Monthly:
        return 30;

      case EnumTemporality.Quarterly:
        return 90;

      case EnumTemporality.Biannual:
        return 180;

      case EnumTemporality.Annual:
        return 360;

      default:
        return 0;
    }
  }
}
